import React, { createRef, SyntheticEvent, useContext } from 'react';
import {
  Button,
  ButtonGroup,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Clear from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import { CalendarSearchField } from '../input';
import * as css from './search.module.scss';
import {
  CalendarNavigation,
  CalendarNavigationContext,
  NavigationActionType,
  ZoomFactor,
} from './useCalendarNavigation';
import { ActivityStatusOptions as UHActivityStatusOptions } from '../../calendaruha/calendarModels';
import { ActivityStatusOptions as PSActivityStatusOptions } from '../../calendarpsa/calendarModels';
import { useFormatMessageFor } from '../../i18n/translation';
import translations from './calendar.i18n';

type ActivityStatusOptions = UHActivityStatusOptions | PSActivityStatusOptions;

const useGetLabelBasedOnActivityStatus = (): ((
  activityStatus: ActivityStatusOptions,
) => string) => {
  const labels = useFormatMessageFor(translations);

  return (activityStatus) => {
    switch (activityStatus) {
      case UHActivityStatusOptions.ALL_ACTIVITIES:
      case PSActivityStatusOptions.ALL_ACTIVITIES:
        return labels.selectActivitystatusDropdownAllActivities;
      case UHActivityStatusOptions.UNFILTERED:
      case PSActivityStatusOptions.UNFILTERED:
        return labels.selectActivitystatusDropdownUnfiltered;
      case UHActivityStatusOptions.ACTIVITIES_NOT_SHARED:
      case PSActivityStatusOptions.ACTIVITIES_NOT_SHARED:
        return labels.selectActivitystatusDropdownActivitiesNotShared;
      case UHActivityStatusOptions.ACTIVITIES_MISSING_NEED:
        return labels.selectActivitystatusDropdownActivitiesMissingNeed;
      case UHActivityStatusOptions.ACTIVITIES_MISSING_CAPACITY:
      case PSActivityStatusOptions.ACTIVITIES_MISSING_CAPACITY:
        return labels.selectActivitystatusDropdownActivitiesMissingCapacity;
      case UHActivityStatusOptions.ACTIVITIES_FILLED_CAPACITY:
      case PSActivityStatusOptions.ACTIVITIES_FILLED_CAPACITY:
        return labels.selectActivitystatusDropdownActivitiesFilledCapacity;
    }
  };
};

interface Props {
  onSearchPhraseChanged: (phrase: string) => void;
  navigation: CalendarNavigation;
  searchCssOutlined: boolean;
  changeSearchCssOutlined: (css: boolean) => void;
  showClearButton: boolean;
  toggleClearButton: (flag: boolean) => void;
  onActivityStatusFilterChanged: Function;
  activityStatusFilter: string;
  searchPhrase: string;
  isStudiested: boolean;
}

const SearchBar: React.FC<React.PropsWithChildren<Props>> = ({
  onSearchPhraseChanged,
  navigation,
  searchCssOutlined,
  changeSearchCssOutlined,
  showClearButton,
  toggleClearButton,
  onActivityStatusFilterChanged,
  activityStatusFilter,
  searchPhrase,
  isStudiested,
}) => {
  const labels = useFormatMessageFor(translations);
  const getLabelBasedOnActivityStatus = useGetLabelBasedOnActivityStatus();

  const inputRef = createRef<HTMLInputElement>();

  const blur = () => {
    (inputRef.current as HTMLInputElement).value === '' &&
      changeSearchCssOutlined(false);
  };

  const keyUp = (event: SyntheticEvent) => {
    (event.target as HTMLInputElement).value
      ? changeSearchCssOutlined(true)
      : changeSearchCssOutlined(false);
    onSearchPhraseChanged((event.target as HTMLInputElement).value);

    if (
      (event.target as HTMLInputElement).value.length > 0 &&
      !showClearButton
    ) {
      toggleClearButton(true);
    } else if (
      (event.target as HTMLInputElement).value.length === 0 &&
      showClearButton
    ) {
      toggleClearButton(false);
    }
  };

  const click = () => {
    (inputRef.current as HTMLInputElement).value = '';
    changeSearchCssOutlined(false);
    onSearchPhraseChanged('');
    toggleClearButton(false);
  };

  const onActivityStatusFilterChange = (event: SelectChangeEvent) =>
    onActivityStatusFilterChanged((event.target as HTMLInputElement).value);

  const onSearchPhraseChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    onSearchPhraseChanged(event.target.value);

  const navigationState = navigation;
  const navigationDispatch = useContext(CalendarNavigationContext);

  return (
    <div className="search-bar">
      <div className="left-dropdown">
        <FormControl margin="normal" fullWidth>
          <Select
            MenuProps={{
              anchorOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
              style: {
                top: '-1px',
                left: '-15px',
              },
              MenuListProps: {
                style: {
                  minWidth: '257px',
                },
              },
            }}
            SelectDisplayProps={{
              style: {
                padding: '16px 25px',
                backgroundColor: 'transparent',
              },
            }}
            className={css.activityStatusFilterSelect}
            disableUnderline
            value={activityStatusFilter}
            onChange={onActivityStatusFilterChange}
          >
            <MenuItem className={css.activityStatusFilterMenuItem} disabled>
              {labels.selectActivitystatusDropdown}
            </MenuItem>
            {Object.keys(
              isStudiested ? UHActivityStatusOptions : PSActivityStatusOptions,
            ).map((optionKey) => {
              const mappedKey = isStudiested
                ? UHActivityStatusOptions[
                    optionKey as keyof typeof UHActivityStatusOptions
                  ]
                : PSActivityStatusOptions[
                    optionKey as keyof typeof PSActivityStatusOptions
                  ];

              return (
                <MenuItem key={optionKey} value={mappedKey}>
                  {getLabelBasedOnActivityStatus(mappedKey)}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </div>
      <div className="search-input-wrapper">
        <FormControl margin="normal" fullWidth>
          <CalendarSearchField
            type="text"
            inputRef={inputRef}
            searchCssOutlined={searchCssOutlined}
            onClick={() => {
              changeSearchCssOutlined(true);
            }}
            onBlur={blur}
            onKeyUp={keyUp}
            onChange={onSearchPhraseChange}
            icon={<SearchIcon className={css.icon} />}
            button={
              showClearButton ? (
                <IconButton
                  className={css.iconButton}
                  onClick={click}
                  size="large"
                >
                  <Clear className={css.clearIcon} />
                </IconButton>
              ) : (
                ''
              )
            }
            defaultValue={searchPhrase}
            placeholder={labels.searchbarSearchinputPlaceholder}
          />
        </FormControl>
      </div>
      <div className="navigation-controls">
        <ButtonGroup
          className={css.buttons}
          size="small"
          aria-label="Calendar navigation"
        >
          <Button
            disabled={navigationState.backwardDisabled}
            className={css.buttonLeft}
            onClick={() =>
              navigationDispatch({ type: NavigationActionType.MOVE_BACK })
            }
          >
            <ArrowLeftIcon />
          </Button>
          <Button
            disabled={navigationState.forwardDisabled}
            className={css.buttonRight}
            onClick={() =>
              navigationDispatch({ type: NavigationActionType.MOVE_FORWARD })
            }
          >
            <ArrowRightIcon />
          </Button>
          {navigationState.zoomFactor === ZoomFactor.WEEKLY ? (
            <Button
              className={css.buttonZoom}
              onClick={() =>
                navigationDispatch({ type: NavigationActionType.ZOOM_IN })
              }
            >
              {labels.showDays}
            </Button>
          ) : (
            <Button
              className={css.buttonZoom}
              onClick={() =>
                navigationDispatch({ type: NavigationActionType.ZOOM_OUT })
              }
            >
              {labels.showWeeks}
            </Button>
          )}
        </ButtonGroup>
      </div>
    </div>
  );
};

export default SearchBar;
